import { Component } from '@theme/component';

/**
 * A simple, accessible tablist that switches which collection's products are shown.
 *
 * Each tab (`role="tab"`) controls one or more panels (elements carrying
 * `data-tab-index` equal to that tab's position) rendered server-side for that
 * collection. Activating a tab just toggles the `hidden` attribute on the matching
 * panels and updates the roving tabindex / aria-selected state - no data is fetched,
 * every panel (carousel) is already rendered server-side.
 *
 * @extends {Component<{tabs: HTMLElement[], panels: HTMLElement[]}>}
 */
export class CollectionTabsComponent extends Component {
  requiredRefs = ['tabs', 'panels'];
  #active = 0;

  /** @type {AbortController | null} */
  #abort = null;

  connectedCallback() {
    super.connectedCallback();

    const { tabs } = this.refs;
    if (!tabs?.length) return;

    this.#active = Math.max(
      0,
      tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true')
    );

    this.#setupEventListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
  }

  #setupEventListeners() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    const opts = { signal: this.#abort.signal };
    const { tabs } = this.refs;

    this.addEventListener('keydown', (event) => this.#handleKeydown(event), opts);

    for (const [index, tab] of tabs.entries()) {
      tab.addEventListener('click', (event) => this.#handleTabClick(event, index), opts);
    }
  }

  /** @param {KeyboardEvent} event */
  #handleKeydown(event) {
    const target = /** @type {HTMLElement} */ (event.target);
    if (target.getAttribute('role') !== 'tab') return;

    const { tabs } = this.refs;
    if (!tabs?.length) return;

    const index = tabs.indexOf(target);
    if (index === -1) return;

    /** @type {Record<string, number>} */
    const navMap = {
      ArrowLeft: -1,
      ArrowRight: 1,
      Home: -index,
      End: tabs.length - 1 - index,
    };

    const offset = navMap[event.key];
    if (offset === undefined) return;

    event.preventDefault();
    const nextIndex = (index + offset + tabs.length) % tabs.length;
    tabs[nextIndex]?.focus();
    this.#activate(nextIndex);
  }

  /**
   * @param {MouseEvent} event
   * @param {number} index
   */
  #handleTabClick(event, index) {
    event.preventDefault();
    this.#activate(index);
  }

  /** @param {number} index */
  #activate(index) {
    if (index === this.#active) return;
    this.#active = index;
    this.#updateActiveTab();
  }

  #updateActiveTab() {
    const { tabs, panels } = this.refs;

    for (const [index, tab] of tabs?.entries() ?? []) {
      const isActive = index === this.#active;
      tab.setAttribute('aria-selected', String(isActive));
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
    }

    // Match by `data-tab-index` rather than array position: a layout can render more
    // than one root element per collection (e.g. a desktop carousel plus a separate
    // mobile-fallback carousel), so `panels` isn't guaranteed to have exactly one
    // entry per tab.
    for (const panel of panels ?? []) {
      const panelIndex = Number(panel.dataset.tabIndex ?? -1);
      panel.toggleAttribute('hidden', panelIndex !== this.#active);
    }
  }
}

customElements.define('collection-tabs-component', CollectionTabsComponent);
